/**
 * GIFT BOX BUILDER - CORE ENGINE
 */
if (typeof GiftBoxBuilder === 'undefined') {

  class GiftBoxBuilder {
    constructor() {
      this.activeBoxConfig = null;
      this.selectedProducts = {}; // Structure: { collection_handle: [products] }
      
      this.init();
    }

    init() {
      this.builderSection = document.getElementById('gift-box-builder');
      if (!this.builderSection) return;

      this.bindEvents();
    }

    bindEvents() {
      // Box Selection
      document.addEventListener('click', (e) => {
        const selectBtn = e.target.closest('[data-action="select-box"]');
        if (selectBtn) {
          this.selectBox(selectBtn.dataset.boxId);
        }

        const closeBtn = e.target.closest('#builder-close-btn');
        if (closeBtn) {
          this.closeBuilder();
        }

        // Product Card Toggle (Select/Deselect)
        const productCard = e.target.closest('.builder__product-card');
        if (productCard) {
          this.toggleProduct(productCard);
        }

        // Tab Switching
        const tabBtn = e.target.closest('.builder__tab');
        if (tabBtn) {
          this.switchTab(tabBtn.dataset.target);
        }
      });

      // Add to Cart
      document.getElementById('builder-add-to-cart')?.addEventListener('click', () => {
        this.addToCart();
      });
    }

    selectBox(boxId) {
      const config = window.GIFT_BOX_CONFIGS[boxId];
      if (!config) return;

      this.activeBoxConfig = config;
      this.selectedProducts = {};
      
      // Initialize buckets for each slot
      config.slotConfigs.forEach(slot => {
        this.selectedProducts[slot.collection_handle] = [];
      });

      this.renderBuilder();
      
      this.builderSection.style.display = 'block';
      this.builderSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderBuilder() {
      const config = this.activeBoxConfig;
      
      document.getElementById('preview-box-title').textContent = config.title;
      document.getElementById('preview-open-box-image').src = config.openBoxImage;
      
      const tabs = document.getElementById('builder-tabs');
      const panels = document.getElementById('builder-tab-panels');
      
      tabs.innerHTML = '';
      panels.innerHTML = '';

      // Sort slots by tab order
      const sortedSlots = [...config.slotConfigs].sort((a, b) => a.tab_order - b.tab_order);

      sortedSlots.forEach((slot, index) => {
        const isActive = index === 0;

        // Create Tab
        const tab = document.createElement('button');
        tab.className = `builder__tab ${isActive ? 'is-active' : ''}`;
        tab.textContent = slot.collection_label;
        tab.dataset.target = slot.collection_handle;
        tab.setAttribute('role', 'tab');
        tabs.appendChild(tab);

        // Create Panel
        const panel = document.createElement('div');
        panel.className = `builder__tab-panel ${isActive ? 'is-active' : ''}`;
        panel.id = `panel-${slot.collection_handle}`;
        panel.dataset.handle = slot.collection_handle;
        
        this.renderProductGrid(slot.collection_handle, panel);
        panels.appendChild(panel);
      });

      this.updateUI();
    }

    renderProductGrid(handle, container) {
      const products = window.GIFT_BOX_PRODUCTS[handle] || [];
      
      if (products.length === 0) {
        container.innerHTML = `<p class="builder__no-products">No products found in this collection.</p>`;
        return;
      }

      container.innerHTML = products.map(p => `
        <div class="builder__product-card" 
             data-variant-id="${p.variantId}" 
             data-handle="${handle}" 
             data-price="${p.price}">
          <div class="builder__product-image-wrap">
            <img src="${p.image}" loading="lazy">
            <div class="builder__product-badge">✓</div>
          </div>
          <h4 class="builder__product-title">${p.title}</h4>
          <p class="builder__product-price">${p.priceFormatted}</p>
        </div>
      `).join('');
    }

    switchTab(handle) {
      document.querySelectorAll('.builder__tab').forEach(t => {
        t.classList.toggle('is-active', t.dataset.target === handle);
      });
      document.querySelectorAll('.builder__tab-panel').forEach(p => {
        p.classList.toggle('is-active', p.dataset.handle === handle);
      });
    }

    toggleProduct(card) {
      const variantId = card.dataset.variantId;
      const handle = card.dataset.handle;
      const slotConfig = this.activeBoxConfig.slotConfigs.find(s => s.collection_handle === handle);
      
      // Check if already selected
      const selectedInSlot = this.selectedProducts[handle];
      const existingIndex = selectedInSlot.findIndex(p => p.variantId == variantId);

      if (existingIndex > -1) {
        // DESELECT logic
        selectedInSlot.splice(existingIndex, 1);
        card.classList.remove('is-selected');
      } else {
        // SELECT logic
        if (selectedInSlot.length < slotConfig.limit) {
          const productData = window.GIFT_BOX_PRODUCTS[handle].find(p => p.variantId == variantId);
          selectedInSlot.push(productData);
          card.classList.add('is-selected');
        } else {
          // Optional: Vibrate or alert that limit is reached
          console.warn("Limit reached for this category");
          return;
        }
      }

      this.updateUI();
    }

    updateUI() {
      const config = this.activeBoxConfig;
      const allSelected = Object.values(this.selectedProducts).flat();
      const totalCount = allSelected.length;
      const totalLimit = config.totalLimit;

      // Update Progress
      const progressText = document.getElementById('builder-progress-text');
      const progressFill = document.getElementById('builder-progress-fill');
      
      if (progressText) progressText.textContent = `${totalCount} of ${totalLimit} items`;
      if (progressFill) progressFill.style.width = `${(totalCount / totalLimit) * 100}%`;

      // Update Thumbnails in Visual Box
      const thumbContainer = document.getElementById('builder-thumbnails');
      thumbContainer.innerHTML = allSelected.map(p => `
        <div class="builder__preview-thumbnail">
          <img src="${p.image}">
        </div>
      `).join('');

      // Update Price
      const itemsPrice = allSelected.reduce((sum, p) => sum + p.price, 0);
      const totalPrice = config.basePrice + itemsPrice;
      document.getElementById('preview-final-total').textContent = `$${(totalPrice / 100).toFixed(2)}`;

      // Update Add to Cart Button
      const atcBtn = document.getElementById('builder-add-to-cart');
      const missingText = document.getElementById('builder-missing-text');
      
      const isComplete = totalCount === totalLimit;
      atcBtn.disabled = !isComplete;

      if (!isComplete) {
        const remaining = totalLimit - totalCount;
        atcBtn.textContent = `Add ${remaining} More Items`;
        missingText.textContent = this.getMissingMessage();
      } else {
        atcBtn.textContent = `Add Gift Box to Cart`;
        missingText.textContent = "";
      }
    }

    getMissingMessage() {
      let missing = [];
      this.activeBoxConfig.slotConfigs.forEach(slot => {
        const count = this.selectedProducts[slot.collection_handle].length;
        if (count < slot.limit) {
          missing.push(slot.collection_label);
        }
      });
      return missing.length > 0 ? "Missing: " + missing.join(", ") : "";
    }

    async addToCart() {
        const btn = document.getElementById('builder-add-to-cart');
        const originalText = btn.textContent;
        
        if (btn.disabled || btn.classList.contains('is-loading')) return;

        btn.textContent = "Adding Your Box...";
        btn.disabled = true;
        btn.classList.add('is-loading');

        // Use a consistent ID format
        const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const items = [];
        
        // 1. Add Parent Gift Box Product
        items.push({
            id: parseInt(this.activeBoxConfig.variantId),
            quantity: 1,
            properties: { 
                '_bundle_id': bundleId, 
                '_is_parent': 'true', // Consistent with your Liquid template
                'Box Name': this.activeBoxConfig.title
            }
        });

        // 2. Add Selected Sub-Products
        Object.values(this.selectedProducts).flat().forEach(p => {
            items.push({
                id: parseInt(p.variantId),
                quantity: 1,
                properties: { 
                    '_bundle_id': bundleId, 
                    '_is_child': 'true',
                    '_parent_box': this.activeBoxConfig.title
                }
            });
        });

        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: items })
            });

            if (response.ok) {
                btn.textContent = "✓ Added!";
                
                // Refresh and Open the Drawer Cart instead of redirecting
                if (typeof DrawerCart !== 'undefined') {
                    DrawerCart.refresh().then(() => {
                        DrawerCart.open();
                        this.closeBuilder(); // Close the builder UI
                    });
                } else {
                    window.location.href = '/cart'; // Fallback
                }
            } else {
                throw new Error('Cart addition failed');
            }
        } catch (err) {
            console.error("Add to Cart Error:", err);
            btn.textContent = "Error - Try Again";
        } finally {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            setTimeout(() => { btn.textContent = originalText; }, 3000);
        }
    }

    closeBuilder() {
      this.builderSection.style.display = 'none';
      this.activeBoxConfig = null;
      document.querySelectorAll('.gift-box-card').forEach(c => c.classList.remove('is-selected'));
    }
  }

  // Initialize
  window.GiftBoxBuilderInstance = new GiftBoxBuilder();
}